import clsx from 'clsx'
import styles from '../ControlSheetCells.module.scss'
import { ControlSheetAnnotation, ControlSheetNote, ControlSheetTrip } from '../../types/types'
import { TripTooltip } from '../../ui/TripTooltip'
import { TripAnnotationTooltip } from '../../ui/TripAnnotationTooltip'
import { DualTooltipMealRelief } from '../../ui/DualTooltipMealRelief'
import { AnnotationsPermission } from '@/pages/ControlSheet/ui/ControlSheetPage'
import { displayTime } from '../../helpers/displayTime'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { ControlSheetDTO } from '@/types/controlsheet'
import ChatIcon from '../../assets/chat.svg'
import EditIcon from '../../assets/edit.svg'

interface Props {
    trip: ControlSheetTrip
    run: string
    route: string
    annotationsPermission: AnnotationsPermission
    refetchControlSheet: (
        options?: RefetchOptions | undefined
    ) => Promise<QueryObserverResult<ControlSheetDTO[], Error>>
    addTempNote: (note: ControlSheetNote) => void
    deleteTempNote: (noteId: string) => void
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void
}

export const LayoverShiftEndCell = ({
    trip,
    run,
    route,
    annotationsPermission,
    refetchControlSheet,
    addTempNote,
    deleteTempNote,
    addTempAnnotation,
}: Props) => {
    // For layover shift end, we don't have a specific breakpoint annotation
    // but we could have a timing annotation if needed
    const annotation = undefined as ControlSheetAnnotation | undefined

    const text = (
        <>
            {'Finish'}
            <br />
            {displayTime(trip.shiftEnd)}
        </>
    )

    const hoverTooltipComponent = (
        <TripTooltip
            tripNumber={trip.trip}
            isNearDrivingHours={trip.isNearDrivingHours}
            plannedTime={trip.shiftEnd}
            notes={annotation?.notes ?? []}
            annotationsPermission={annotationsPermission}
        />
    )

    const clickTooltipComponent = (
        <TripAnnotationTooltip
            breakpoint=""
            duty={trip.duty}
            trip={trip.trip}
            run={run}
            route={route}
            scheduled={trip.shiftEnd}
            index={-1} // Special index for layover shift end
            onUpdate={refetchControlSheet}
            addTempNote={addTempNote}
            deleteTempNote={deleteTempNote}
            addTempAnnotation={addTempAnnotation}
            contextType="Departure"
            annotation={annotation}
        />
    )

    const child = (
        <div
            className={clsx(styles.cell, {
                [styles.cellEdit]: annotationsPermission === 'edit',
                [styles.shiftEndTime]: true,
            })}
        >
            {text}

            {annotation && annotation.notes.length > 0 && (
                <div className={styles.commentIcon}>
                    <ChatIcon />
                </div>
            )}

            <div className={styles.editIcon}>
                <EditIcon />
            </div>
        </div>
    )

    return (
        <DualTooltipMealRelief
            tooltip={hoverTooltipComponent}
            clickTooltip={clickTooltipComponent}
            trigger={annotationsPermission === 'edit' ? 'hover+click' : 'hover'}
            placement="bottom"
        >
            {child}
        </DualTooltipMealRelief>
    )
}
